// Default-off staff preview entry. Installing only observes an already
// authenticated native snapshot. It never claims or mutates a cart itself.
import { bootstrapVoucherRedemption } from './loyalty-voucher-bootstrap.js?v=20261012-stacking-prepare';
import { createVoucherHttp } from './loyalty-voucher-http.js?v=20261012-stacking-prepare';
import { parseVoucherCatalogue } from './loyalty-voucher-catalogue.js?v=m6-20261002-catalogue';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const installations = new WeakMap();
const AUTHENTICATED_SUMMARY = 'sigma-loyalty-summary[data-authenticated="true"]';

// Diagnostics are deliberately a fixed, non-sensitive vocabulary. They expose
// no response body, customer identity, provider error or voucher operation.
const setEntryState = (document, state, source) => {
  const summaries = typeof document?.querySelectorAll === 'function'
    ? [...document.querySelectorAll(AUTHENTICATED_SUMMARY)]
    : [];
  if (source?.ownerDocument === document && typeof source.matches === 'function'
    && source.matches(AUTHENTICATED_SUMMARY) && !summaries.includes(source)) summaries.push(source);
  for (const summary of summaries) summary.setAttribute('data-loyalty-voucher-entry-state', state);
};

const setEntryReason = (document, reason, source) => {
  const summaries = typeof document?.querySelectorAll === 'function'
    ? [...document.querySelectorAll(AUTHENTICATED_SUMMARY)]
    : [];
  if (source?.ownerDocument === document && typeof source.matches === 'function'
    && source.matches(AUTHENTICATED_SUMMARY) && !summaries.includes(source)) summaries.push(source);
  for (const summary of summaries) summary.setAttribute('data-loyalty-voucher-entry-reason', reason);
};

export const classifyVoucherEntryBootstrapFailure = (error) => {
  if (!(error instanceof Error)) return 'other';
  const message = error.message;
  if (message === 'Invalid voucher reward catalogue.') return 'catalogue';
  if (message === 'Invalid voucher bootstrap configuration.'
    || message === 'Invalid voucher reward inventory.'
    || message.startsWith('Unsupported voucher bootstrap option:')) return 'config';
  if (message === 'Session storage is required for voucher requests.'
    || message === 'Invalid voucher reward inventory'
    || message.startsWith('Missing voucher adapter:')) return 'session';
  if (message === 'A reward root and session are required.') return 'view';
  if (message === 'A voucher session is already mounted in this document') return 'duplicate';
  if (message === 'A valid Shopify cart adapter environment is required.'
    || message === 'The Shopify routes root must be a same-origin locale path.'
    || message === 'Invalid voucher bootstrap document.'
    || message === 'Invalid voucher bootstrap environment.') return 'environment';
  if (error instanceof ReferenceError) return 'reference';
  if (error instanceof RangeError) return 'range';
  if (error instanceof TypeError) return 'type';
  return 'other';
};

const validNativeSnapshot = (snapshot) => snapshot?.schemaVersion === 1
  && snapshot.provider === 'loyaltylion'
  && snapshot.available === true
  && snapshot.stale !== true
  && Number.isSafeInteger(snapshot.approvedPoints)
  && snapshot.currencyCode === 'GBP'
  && snapshot.preview !== true
  && typeof snapshot.voucherClaimingAvailable === 'boolean';

export const updateActiveVoucherSession = (session, snapshot) => {
  session?.updateSavedVouchers?.(snapshot?.savedVouchers);
  const catalogue = parseVoucherCatalogue(snapshot?.voucherRewards);
  session?.updateVoucherRewards?.(catalogue || []);
  session?.updateClaimingAvailable?.(snapshot?.voucherClaimingAvailable === true);
  session?.updateStackingPreparationAvailable?.(snapshot?.voucherStackingPreparationAvailable === true);
  return catalogue !== null;
};

export const disableActiveVoucherSession = (session) => {
  session?.updateVoucherRewards?.([]);
  session?.updateClaimingAvailable?.(false);
  session?.updateStackingPreparationAvailable?.(false);
};

const validDiscovery = (result) => result?.status === 'none'
  || (
    result?.status === 'outstanding'
    && typeof result.operation?.requestId === 'string'
    && UUID.test(result.operation.requestId)
  );

const inactive = Object.freeze({ active: false, dispose() {} });

export const installVoucherEntry = ({ document = globalThis.document, window = globalThis.window } = {}) => {
  if (!document || !window || window.Shopify?.designMode === true) return inactive;
  const existing = installations.get(document);
  if (existing) return existing;

  let http;
  try {
    http = createVoucherHttp({
      origin: window.location?.origin,
      fetchImpl: window.fetch?.bind(window),
    });
  } catch {
    return inactive;
  }
  setEntryState(document, 'waiting');

  let attempted = false;
  let disposed = false;
  let activeHandle = null;
  const onSnapshot = async (event) => {
    const summary = event.target;
    if (
      disposed
      || summary?.ownerDocument !== document
      || typeof summary.matches !== 'function'
      || !summary.matches('sigma-loyalty-summary[data-authenticated="true"]')
      || !validNativeSnapshot(event.detail)
    ) {
      return;
    }

    if (activeHandle) {
      updateActiveVoucherSession(activeHandle.session, event.detail);
      return;
    }
    if (attempted) return;

    const voucherRewards = parseVoucherCatalogue(event.detail.voucherRewards);
    if (!voucherRewards) {
      setEntryState(document, 'bootstrap_unavailable', summary);
      setEntryReason(document, 'catalogue', summary);
      return;
    }

    attempted = true;
    setEntryState(document, 'discovering', summary);
    let discovery;
    const hasInventory = event.detail.savedVouchers?.status === 'ready'
      && Array.isArray(event.detail.savedVouchers.items)
      && typeof event.detail.voucherClaimingAvailable === 'boolean';
    if (hasInventory && event.detail.voucherClaimingAvailable === false) {
      discovery = { status: 'none' };
    } else {
      try {
        discovery = await http.discover();
      } catch {
        if (!disposed) attempted = false;
        setEntryState(document, 'discovery_unavailable', summary);
        return;
      }
    }
    if (disposed) return;
    if (summary.isConnected === false) {
      attempted = false;
      return;
    }
    if (!validDiscovery(discovery)) {
      attempted = false;
      setEntryState(document, 'discovery_invalid', summary);
      return;
    }

    summary.setAttribute('data-loyalty-voucher-staff', 'true');
    summary.setAttribute('data-environment', 'production');
    try {
      const handle = bootstrapVoucherRedemption({
        config: { enabled: true,
          voucherRewards,
          claimingAvailable: event.detail.voucherClaimingAvailable,
          stackingPreparationAvailable: event.detail.voucherStackingPreparationAvailable === true,
          ...(hasInventory ? { savedVouchers: event.detail.savedVouchers } : {}),
        },
        document,
      });
      setEntryState(document, handle.active === true ? 'mounted' : 'bootstrap_unavailable', summary);
      if (handle.active === true) activeHandle = handle;
      if (handle.active !== true) {
        attempted = false;
        setEntryReason(document, 'other', summary);
      }
    } catch (error) {
      attempted = false;
      summary.removeAttribute?.('data-loyalty-voucher-staff');
      summary.removeAttribute?.('data-environment');
      setEntryState(document, 'bootstrap_unavailable', summary);
      setEntryReason(document, classifyVoucherEntryBootstrapFailure(error), summary);
    }
  };

  document.addEventListener('sigma:loyalty-snapshot', onSnapshot);
  const onUnavailable = (event) => {
    const summary = event.target;
    if (!activeHandle || summary?.ownerDocument !== document
      || typeof summary.matches !== 'function' || !summary.matches(AUTHENTICATED_SUMMARY)) return;
    disableActiveVoucherSession(activeHandle.session);
  };
  document.addEventListener('sigma:loyalty-unavailable', onUnavailable);
  let lastRefresh = 0;
  const refreshOnReturn = () => {
    if (disposed || document.visibilityState === 'hidden' || Date.now() - lastRefresh < 2000) return;
    lastRefresh = Date.now();
    for (const summary of document.querySelectorAll(AUTHENTICATED_SUMMARY)) {
      summary.load?.({ background: true });
    }
  };
  const refreshOnExpand = (event) => {
    if (disposed || Date.now() - lastRefresh < 2000) return;
    const toggle = event.target?.closest?.('[data-loyalty-toggle]');
    const summary = toggle?.closest?.(AUTHENTICATED_SUMMARY);
    if (!summary || toggle.getAttribute?.('aria-expanded') !== 'true') return;
    lastRefresh = Date.now();
    summary.load?.({ background: true });
  };
  window.addEventListener?.('focus', refreshOnReturn);
  document.addEventListener('visibilitychange', refreshOnReturn);
  // The summary toggle updates aria-expanded before this bubbling listener.
  // Refreshing on reopen prevents a long-lived page from retaining an old
  // provider catalogue while preserving the existing panel and review state.
  document.addEventListener('click', refreshOnExpand);
  const installation = {
    active: true,
    dispose() {
      if (disposed) return;
      disposed = true;
      document.removeEventListener('sigma:loyalty-snapshot', onSnapshot);
      document.removeEventListener('sigma:loyalty-unavailable', onUnavailable);
      document.removeEventListener('visibilitychange', refreshOnReturn);
      document.removeEventListener('click', refreshOnExpand);
      window.removeEventListener?.('focus', refreshOnReturn);
      activeHandle?.dispose();
    },
  };
  installations.set(document, installation);
  // The native balance can finish before this module and its imports load.
  // Refresh only after the listener and singleton are ready; this is read-only.
  refreshOnReturn();
  return installation;
};

if (globalThis.document && globalThis.window) {
  installVoucherEntry();
}
